import _ from 'lodash';
import { createSelector } from 'reselect';

import CampaignsAx       from 'app/actions/campaigns';
import CadminCompaniesAx from 'app/actions/company-admin/companies';
import {
  Pages,
  CampaignTempTypes as TempTypes,
}                        from 'app/constants';
import reducerUtils      from 'app/reducers/utils';
import EntitiesSlx       from 'app/selectors/entities';
import RoutingSlx        from 'app/selectors/routing';



/*
 *  Actions
 */

const Types = {
  LOAD: 'CADMIN_PAGE_CAMPS_TEMPLATES_LOAD',
};

const Ax = {

  load: ({params, query}) => (dispatch, getState) => {
    const alreadyOnPage = RoutingSlx.page(getState()) === Pages.CADMIN_CAMPAIGNS_TEMPLATES;
    if (alreadyOnPage) return;
    const promise = Promise.all([
      dispatch(CampaignsAx.fetchTemplates()),
      dispatch(CadminCompaniesAx.loadCommon(params.companySlug)),
    ]);
    return dispatch({type: Types.LOAD, promise});
  },

};



/*
 *  Reducer
 */

const initialState = {
  isLoading: false,
  templateCampaignIds: null,
};

const reducer = reducerUtils.createReducer(initialState, {

  [`${Types.LOAD}_PENDING`]: (state, action) => {
    return {...state,
      isLoading: true,
      templateCampaignIds: null,
    };
  },
  [`${Types.LOAD}_RESOLVED`]: (state, action) => {
    const templateCampaignIds = _.get(action, 'result[0].campaigns').map(c => c.id);
    return {...state,
      isLoading: false,
      templateCampaignIds,
    };
  },
  [`${Types.LOAD}_REJECTED`]: (state, action) => {
    return {...state,
      isLoading: false,
    };
  },

});



/*
 *  Selectors
 */

const campaignMatchesSearch = (campaign, dirtySearch) => {
  let cleanSearch = (dirtySearch || '').trim().toUpperCase();
  if ((campaign.name || '').toUpperCase().includes(cleanSearch)) return true;
  if ((campaign.description || '').toUpperCase().includes(cleanSearch)) return true;
  return false;
};

const pageSize = 30;

const Slx = (() => {

  const selIsLoading = (state) => state.companyAdmin.pageCampaignsTemplates.isLoading;
  const selTemplateCampaignIds = state => state.companyAdmin.pageCampaignsTemplates.templateCampaignIds;

  const getCampaigns = (ids, campaigns, nonprofits) => {
    return ids && ids.map((id) => {
      const campaign = {...campaigns[id]};
      campaign.aNonprofit = nonprofits[campaign.aNonprofitId];
      return campaign;
    });
  };

  const selAllCampaigns = createSelector(
    [selTemplateCampaignIds, EntitiesSlx.campaigns, EntitiesSlx.nonprofits],
    (ids, campaignMap, nonprofitMap) => {
      return ids && ids.map((id) => {
        const campaign = {...campaignMap[id]};
        campaign.aNonprofit = nonprofitMap[campaign.aNonprofitId];
        return campaign;
      });
    }
  );

  const selParams = createSelector(
    [RoutingSlx.query],
    (query) => {
      const page = (() => {
        if (!query.page) return null;
        const pageInt = parseInt(query.page);
        if (!_.isFinite(pageInt)) return null;
        if (pageInt <= 1) return null;
        return pageInt;
      })();
      const obj = {
        nteeCode: query.nteeCode || null,
        search: query.search || null,
        type: Object.values(TempTypes).includes(query.type) ? query.type : null,
        activity: ['give', 'vol', 'drive'].includes(query.activity) ? query.activity : null,
        page,
      };
      return obj;
    }
  );

  const selFilteredCampaigns = createSelector(
    [selAllCampaigns, selParams],
    (campaigns, params) => {
      if (!campaigns) return null;
      return campaigns.filter((camp) => {
        if (params.nteeCode && !(camp.nteeCode || '').startsWith(params.nteeCode)) return false;
        if (params.type && camp.temporalityType !== params.type) return false;
        if (!campaignMatchesSearch(camp, params.search)) return false;
        if (params.activity === 'give'  && !camp.hasGive)  return false;
        if (params.activity === 'drive' && !camp.hasDrive) return false;
        if (params.activity === 'vol'   && !camp.hasVol)   return false;
        return true;
      });
    }
  );

  const selPagination = createSelector(
    [selFilteredCampaigns, selParams],
    (campaigns, params) => {
      if (!campaigns) return null;
      const resultCount = campaigns.length;
      const page = params.page || 1;
      const pageCount = Math.ceil(resultCount / pageSize);
      return {resultCount, pageSize, page, pageCount};
    }
  );

  const selPagedCampaigns = createSelector(
    [selFilteredCampaigns, selPagination],
    (campaigns, pag) => {
      if (!campaigns) return null;
      const start = (pag.page - 1) * pag.pageSize;
      const end = start + pag.pageSize;
      return campaigns.slice(start, end);
    }
  );

  return {
    isLoading: selIsLoading,
    allCampaigns: selAllCampaigns,
    filteredCampaigns: selFilteredCampaigns,
    pagedCampaigns: selPagedCampaigns,
    params: selParams,
    pagination: selPagination,
  };

})();



export {Types, Ax, reducer, Slx};
export default {Types, Ax, reducer, Slx};
